/**
 * IP Tracker utility for visitor analytics
 * Uses ipify API to get visitor IP address
 */

/**
 * Get the visitor's IP address using ipify API
 * @returns {Promise<Object>} - Object containing IP address and related info
 */
/**
 * Extract UTM parameters and other tracking data from URL
 * @returns {Object} - Object containing UTM parameters
 */
export const getTrackingParameters = () => {
  const url = new URL(window.location.href);
  const params = url.searchParams;
  
  return {
    utm_source: params.get('utm_source') || null,
    utm_medium: params.get('utm_medium') || null,
    utm_campaign: params.get('utm_campaign') || null,
    utm_term: params.get('utm_term') || null,
    utm_content: params.get('utm_content') || null,
    gclid: params.get('gclid') || null, // Google Click ID
    fbclid: params.get('fbclid') || null, // Facebook Click ID
    referrer: document.referrer || 'direct',
  };
};

export const getVisitorIp = async () => {
  try {
    // Use ipify API to get IP address (free service with generous limits)
    const response = await fetch('https://api.ipify.org?format=json');
    const data = await response.json();
    
    // Get additional browser information
    const browserInfo = {
      userAgent: navigator.userAgent,
      language: navigator.language,
      platform: navigator.platform,
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      timestamp: new Date().toISOString(),
    };
    
    // Get tracking parameters from URL
    const trackingParams = getTrackingParameters();
    
    return {
      ip: data.ip,
      ...browserInfo,
      ...trackingParams
    };
  } catch (error) {
    console.error('Error getting visitor IP:', error);
    
    // Return partial data even if IP fetch fails
    return {
      ip: null,
      userAgent: navigator.userAgent,
      language: navigator.language,
      platform: navigator.platform,
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      referrer: document.referrer || 'direct',
      timestamp: new Date().toISOString(),
      error: 'Failed to fetch IP address'
    };
  }
};

/**
 * Detect potential bot based on user agent and behavior patterns
 * @param {Object} visitorData - Visitor data including user agent
 * @returns {Object} - Bot detection results
 */
export const detectBot = (visitorData) => {
  const { userAgent } = visitorData;
  const lowerUA = userAgent.toLowerCase();
  
  // Common bot identifiers in user agent
  const botPatterns = [
    'bot', 'crawler', 'spider', 'headless', 'selenium', 'puppeteer',
    'phantomjs', 'chrome-lighthouse', 'googlebot', 'yandexbot',
    'bingbot', 'baiduspider', 'facebookexternalhit', 'twitterbot'
  ];
  
  // Check for bot patterns in user agent
  const matchedPattern = botPatterns.find(pattern => lowerUA.includes(pattern));
  
  // Check for suspicious behavior
  const suspiciousBehavior = {
    noLanguageHeader: !visitorData.language,
    unusualScreen: visitorData.screenWidth < 100 || visitorData.screenHeight < 100,
  };
  
  const isSuspicious = Object.values(suspiciousBehavior).some(Boolean);
  
  return {
    isLikelyBot: !!matchedPattern || isSuspicious,
    botType: matchedPattern || null,
    suspiciousBehavior: isSuspicious ? suspiciousBehavior : null,
  };
};
